import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Save, Play } from 'lucide-react';

export default function Automation() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const data = await api.getAutomationConfig();
      setConfig(data.config);
    } catch (error) {
      console.error('Failed to load config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateAutomationConfig(config);
      alert('Konfigurace byla úspěšně uložena!');
    } catch (error) {
      alert('Chyba při ukládání: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      await api.testAutomation();
      alert('Testovací běh byl spuštěn!');
    } catch (error) {
      alert('Chyba při testování: ' + error.message);
    }
  };

  if (loading) {
    return <div>Načítání...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Automatizace</h1>
        <div className="flex items-center space-x-3">
          <button onClick={handleTest} className="btn btn-secondary flex items-center space-x-2">
            <Play size={20} />
            <span>Test Run</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Save size={20} />
            <span>{saving ? 'Ukládám...' : 'Uložit'}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Základní nastavení */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Základní nastavení</h2>

          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={config?.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                  className="rounded"
                />
                <span className="font-medium">Zapnout automatizaci</span>
              </label>
              <p className="text-sm text-gray-600 mt-1">
                Automaticky generovat a publikovat newslettery podle plánu
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Mód</label>
              <select
                value={config?.mode}
                onChange={(e) => setConfig({ ...config, mode: e.target.value })}
                className="input"
              >
                <option value="manual">Manuální</option>
                <option value="auto">Automatický</option>
              </select>
            </div>
          </div>
        </div>

        {/* Plánování */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Plánování</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Časová zóna</label>
              <input
                type="text"
                value={config?.schedule?.timezone}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    schedule: { ...config.schedule, timezone: e.target.value }
                  })
                }
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Čas</label>
              <input
                type="time"
                value={config?.schedule?.time}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    schedule: { ...config.schedule, time: e.target.value }
                  })
                }
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Dny v týdnu</label>
              <div className="flex flex-wrap gap-2">
                {['Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota', 'Neděle'].map(
                  (day, index) => {
                    const dayValue = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][index];
                    return (
                      <label key={day} className="flex items-center space-x-1">
                        <input
                          type="checkbox"
                          checked={config?.schedule?.days?.includes(dayValue)}
                          onChange={(e) => {
                            const days = e.target.checked
                              ? [...(config.schedule.days || []), dayValue]
                              : config.schedule.days.filter(d => d !== dayValue);
                            setConfig({
                              ...config,
                              schedule: { ...config.schedule, days }
                            });
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{day}</span>
                      </label>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Filtry */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Filtry emailů</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Minimální počet emailů
              </label>
              <input
                type="number"
                value={config?.filters?.minEmails}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    filters: { ...config.filters, minEmails: parseInt(e.target.value) }
                  })
                }
                min="1"
                className="input"
              />
              <p className="text-xs text-gray-600 mt-1">
                Spustit pouze pokud je alespoň tento počet emailů
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Maximální počet emailů
              </label>
              <input
                type="number"
                value={config?.filters?.maxEmails}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    filters: { ...config.filters, maxEmails: parseInt(e.target.value) }
                  })
                }
                min="1"
                max="100"
                className="input"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={config?.filters?.onlyUnanalyzed}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      filters: { ...config.filters, onlyUnanalyzed: e.target.checked }
                    })
                  }
                  className="rounded"
                />
                <span className="text-sm">Pouze neanalyzované emaily</span>
              </label>
            </div>
          </div>
        </div>

        {/* Beehiiv */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Beehiiv publikování</h2>

          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={config?.beehiiv?.autoPublish}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      beehiiv: { ...config.beehiiv, autoPublish: e.target.checked }
                    })
                  }
                  className="rounded"
                />
                <span className="font-medium">Automaticky publikovat</span>
              </label>
              <p className="text-sm text-gray-600 mt-1">
                Publikovat vygenerovaný newsletter na Beehiiv
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Výchozí status</label>
              <select
                value={config?.beehiiv?.defaultStatus}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    beehiiv: { ...config.beehiiv, defaultStatus: e.target.value }
                  })
                }
                className="input"
              >
                <option value="draft">Draft (koncept)</option>
                <option value="confirmed">Confirmed (publikováno)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Počet pokusů při chybě
              </label>
              <input
                type="number"
                value={config?.beehiiv?.retryAttempts}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    beehiiv: { ...config.beehiiv, retryAttempts: parseInt(e.target.value) }
                  })
                }
                min="1"
                max="10"
                className="input"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
